import React from 'react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../ui/tabs';
import { useLanguageStore, getLocalized } from '../../store';
import { useTranslation } from '../../i18n/translations';

const RAW_TAGS_MAP = {
  en: {
    wood: 'Solid Wood', mdf: 'MDF Panels', chipboard: 'Chipboard', melamine: 'Melamine', acrylic: 'Acrylic',
    aluminum: 'Aluminum Profile', glass: 'Premium Glass', mirror: 'Mirror', stone: 'Natural Stone',
    natural_veneer: 'Natural Veneer', engineered_veneer: 'Modified Veneer', hpl: 'HPL Panels',
    leather: 'Natural Leather', eco_leather: 'Eco Leather', fabric: 'Fabric / Textiles',
    primer: 'Primer', paint: 'Paint', lacquer: 'Lacquer',
    matte: 'Matte', glossy: 'Glossy', satin: 'Satin', textured: 'Textured', natural: 'Natural', brushed: 'Brushed'
  },
  ru: {
    wood: 'Натуральное Дерево', mdf: 'МДФ Панели', chipboard: 'ДСП / ЛДСП', melamine: 'Меламин', acrylic: 'Акрил',
    aluminum: 'Алюминиевый Профиль', glass: 'Премиальное Стекло', mirror: 'Зеркальное Полотно', stone: 'Натуральный Камень',
    natural_veneer: 'Натуральный Шпон', engineered_veneer: 'Модифицированный Шпон', hpl: 'HPL Панели',
    leather: 'Натуральная Кожа', eco_leather: 'Экокожа', fabric: 'Интерьерный Текстиль',
    primer: 'Архитектурный Грунт', paint: 'Полиуретановая Эмаль', lacquer: 'Защитный Лак',
    matte: 'Матовая', glossy: 'Глянцевая', satin: 'Сатинированная (Сатин)', textured: 'Текстурированная', natural: 'Натуральная фактура', brushed: 'Брашированная'
  },
  nl: {
    wood: 'Massief Hout', mdf: 'MDF Panelen', chipboard: 'Spaanplaat', melamine: 'Melamine', acrylic: 'Acryl',
    aluminum: 'Aluminium', glass: 'Premium Glas', mirror: 'Spiegel', stone: 'Natuursteen',
    natural_veneer: 'Natuurlijk Fineer', engineered_veneer: 'Gemodificeerd Fineer', hpl: 'HPL Panelen',
    leather: 'Natuurlijk Leer', eco_leather: 'Eco-leer', fabric: 'Stof / Textiel',
    primer: 'Primer', paint: 'Polyurethaanlak', lacquer: 'Beschermende Lak',
    matte: 'Mat', glossy: 'Glanzend', satin: 'Satijn', textured: 'Getextureerd', natural: 'Natuurlijk', brushed: 'Geborsteld'
  },
  ro: {
    wood: 'Lemn Masiv', mdf: 'Panouri MDF', chipboard: 'PAL', melamine: 'Melamină', acrylic: 'Acrilic',
    aluminum: 'Aluminiu', glass: 'Sticlă Premium', mirror: 'Oglindă', stone: 'Piatră Naturală',
    natural_veneer: 'Furnir Natural', engineered_veneer: 'Furnir Modificat', hpl: 'Panouri HPL',
    leather: 'Piele Naturală', eco_leather: 'Piele Ecologică', fabric: 'Material / Textile',
    primer: 'Grund', paint: 'Vopsea Poliuretanică', lacquer: 'Lac Protecție',
    matte: 'Mată', glossy: 'Lucioasă', satin: 'Satinată', textured: 'Texturată', natural: 'Naturală', brushed: 'Satinat'
  }
};

/**
 * 🔥 Безопасное преобразование любых сущностей в строковый вид (Исключает Error #284)
 */
const toStr = (val, lang = 'en') => {
  if (val === null || val === undefined) return '';
  if (typeof val === 'string' || typeof val === 'number') return String(val);
  if (Array.isArray(val)) return val.map((v) => toStr(v, lang)).filter(Boolean).join(', ');
  if (typeof val === 'object') {
    if (val[lang]) return toStr(val[lang], lang);
    if (val.en) return toStr(val.en, lang);
    if (val.ru) return toStr(val.ru, lang);
    if (val.nl) return toStr(val.nl, lang);
    if (val.ro) return toStr(val.ro, lang);
    
    if (val.width || val.height || val.depth || val.w || val.h || val.d) {
      const w = val.width || val.w || '';
      const h = val.height || val.h || '';
      const d = val.depth || val.d || '';
      return [w, h, d].filter(Boolean).join(' × ');
    }
    
    const firstStr = Object.values(val).find((v) => typeof v === 'string');
    return firstStr ? String(firstStr) : '';
  }
  return String(val);
};

export const ProductSpecs = ({ product = {}, labels = {} }) => {
  const { language } = useLanguageStore();
  const { t } = useTranslation(language);
  const currentTokens = RAW_TAGS_MAP[language] || RAW_TAGS_MAP['en'];

  const lblCategory = toStr(labels?.category, language) || 'Category';
  const lblCategoryVal = toStr(labels?.categoryValue, language) || '—';

  const lblSku = toStr(getLocalized(product?.sku_label, language), language) || 
                 toStr(t?.product?.skuLabel, language) || 
                 ({ en: 'Model SKU', ru: 'Артикул модели', nl: 'Model SKU', ro: 'Cod SKU Model' }[language] || 'Model SKU');

  const lblBaseMaterial = toStr(getLocalized(product?.base_material_label, language), language) || 
                          toStr(t?.product?.baseMaterial, language) || 
                          ({ en: 'Current Base Material', ru: 'Текущий базовый материал', nl: 'Huidig basismateriaal', ro: 'Material de bază curent' }[language] || 'Current Base Material');

  const lblBaseCoating = toStr(getLocalized(product?.base_coating_label, language), language) || 
                         toStr(t?.product?.baseCoating, language) || 
                         ({ en: 'Current Base Coating', ru: 'Текущее базовое покрытие', nl: 'Huidige basiscoating', ro: 'Finisaj de bază curent' }[language] || 'Current Base Coating');

  const lblAltMaterials = toStr(getLocalized(product?.alt_materials_label, language), language) || 
                          toStr(t?.product?.altMaterials, language) || 
                          ({ en: 'Alternative Available Materials:', ru: 'Альтернативные доступные материалы:', nl: 'Alternatieve beschikbare materialen:', ro: 'Materiale alternative disponibile:' }[language] || 'Alternative Available Materials:');

  const lblAltFinishes = toStr(getLocalized(product?.alt_finishes_label, language), language) || 
                         toStr(t?.product?.altFinishes, language) || 
                         ({ en: 'Alternative Available Finishes / Textures:', ru: 'Альтернативные доступные отделки / текстуры:', nl: 'Alternatieve beschikbare afwerkingen / texturen:', ro: 'Finisaje / Texturi alternative disponibile:' }[language] || 'Alternative Available Finishes / Textures:');

  const dynamicMatrixTitle = toStr(getLocalized(product?.matrix_title, language), language) || toStr(t?.product?.matrixTitle, language) || 'Bespoke Production Matrix';
  const dynamicMatrixSub = toStr(getLocalized(product?.matrix_sub, language), language) || toStr(t?.product?.matrixSub, language) || 'This system is modular and can be custom ordered in alternative executions.';
  const dynamicCustomSizesTitle = toStr(getLocalized(product?.custom_sizes_title, language), language) || toStr(t?.product?.customSizesTitle, language) || 'Bespoke Size Upon Request';
  const dynamicCustomSizesDesc = toStr(getLocalized(product?.custom_sizes_desc, language), language) || toStr(t?.product?.customSizesDesc, language) || 'Available in custom configurations and floor-to-ceiling sheets up to 300cm.';

  const skuVal = toStr(product?.sku, language) || '—';
  const baseMatVal = toStr(getLocalized(product?.material, language), language) || '—';
  const baseFinishVal = toStr(getLocalized(product?.finish, language), language) || '—';

  const tabSpecsLabel = toStr(t?.common?.specifications, language) || 'Specifications';
  const tabDimLabel = toStr(t?.common?.dimensions, language) || 'Dimensions';

  const isDimensionsObject = product?.dimensions && typeof product.dimensions === 'object' && !Array.isArray(product.dimensions);
  const isDimensionsString = typeof product?.dimensions === 'string' && product.dimensions.trim() !== '';

  return (
    <div className="mt-24 text-left font-light animate-in fade-in duration-500 select-none">
      <Tabs defaultValue="specifications">
        <TabsList className="border-b border-border w-full justify-start rounded-none bg-transparent p-0 h-auto gap-2">
          <TabsTrigger 
            value="specifications" 
            className="rounded-none border-b-2 border-transparent data-[state=active]:border-black data-[state=active]:bg-transparent px-6 py-4 text-xs tracking-widest uppercase font-medium transition-all"
          >
            {tabSpecsLabel}
          </TabsTrigger>
          <TabsTrigger 
            value="dimensions" 
            className="rounded-none border-b-2 border-transparent data-[state=active]:border-black data-[state=active]:bg-transparent px-6 py-4 text-xs tracking-widest uppercase font-medium transition-all"
          >
            {tabDimLabel}
          </TabsTrigger>
        </TabsList>
        
        {/* ВКЛАДКА 1: ХАРАКТЕРИСТИКИ */}
        <TabsContent value="specifications" className="pt-10 space-y-12">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-12 gap-y-8 max-w-2xl border-b border-gray-100 pb-10">
            <div>
              <p className="text-[10px] tracking-wider uppercase text-muted-foreground mb-1.5">{lblCategory}</p>
              <p className="text-sm text-gray-900 font-normal">{lblCategoryVal}</p>
            </div>
            <div>
              <p className="text-[10px] tracking-wider uppercase text-muted-foreground mb-1.5">{lblSku}</p>
              <p className="text-sm font-mono text-gray-900 font-normal">{skuVal}</p>
            </div>
            <div>
              <p className="text-[10px] tracking-wider uppercase text-muted-foreground mb-1.5">{lblBaseMaterial}</p>
              <p className="text-sm text-gray-900">{baseMatVal}</p>
            </div>
            <div>
              <p className="text-[10px] tracking-wider uppercase text-muted-foreground mb-1.5">{lblBaseCoating}</p>
              <p className="text-sm text-gray-900">{baseFinishVal}</p>
            </div>
          </div>

          {/* АЛЬТЕРНАТИВНЫЕ ВАРИАНТЫ МАТЕРИАЛОВ И ОТДЕЛОК */}
          {((Array.isArray(product?.materials) && product.materials.length > 0) || (Array.isArray(product?.finishes) && product.finishes.length > 0)) && (
            <div className="space-y-6 pt-2 animate-in fade-in slide-in-from-bottom-2 duration-500">
              <div>
                <h4 className="text-xs uppercase font-medium tracking-widest text-black mb-1">
                  {dynamicMatrixTitle}
                </h4>
                <p className="text-[11px] text-muted-foreground font-light leading-relaxed">
                  {dynamicMatrixSub}
                </p>
              </div>

              {Array.isArray(product?.materials) && product.materials.length > 0 && (
                <div className="space-y-2">
                  <p className="text-[10px] uppercase tracking-wider text-muted-foreground">{lblAltMaterials}</p>
                  <div className="flex flex-wrap gap-1.5">
                    {product.materials.map((matItem, idx) => {
                      const matKey = toStr(matItem, language);
                      const displayMat = currentTokens[matKey] || matKey;
                      return (
                        <span key={matKey || idx} className="px-3 py-1.5 bg-neutral-50 border border-gray-200 text-gray-800 text-[11px] tracking-wide font-normal">
                          {toStr(displayMat, language)}
                        </span>
                      );
                    })}
                  </div>
                </div>
              )}

              {Array.isArray(product?.finishes) && product.finishes.length > 0 && (
                <div className="space-y-2 pt-1">
                  <p className="text-[10px] uppercase tracking-wider text-muted-foreground">{lblAltFinishes}</p>
                  <div className="flex flex-wrap gap-1.5">
                    {product.finishes.map((finItem, idx) => {
                      const finKey = toStr(finItem, language);
                      const displayFin = currentTokens[finKey] || finKey;
                      return (
                        <span key={finKey || idx} className="px-3 py-1.5 bg-neutral-50 border border-gray-200 text-gray-800 text-[11px] tracking-wide font-normal">
                          {toStr(displayFin, language)}
                        </span>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
          )}
        </TabsContent>
        
        {/* ВКЛАДКА 2: ГАБАРИТЫ */}
        <TabsContent value="dimensions" className="pt-10 space-y-10">
          {isDimensionsObject && Object.keys(product.dimensions).length > 0 ? (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-8 max-w-2xl border-b border-gray-100 pb-10">
              {Object.entries(product.dimensions).map(([key, value]) => {
                const lowerKey = key.toLowerCase();
                const isWeight = lowerKey === 'weight';
                const unit = isWeight ? 'kg' : 'cm';
                const localizedLabel = toStr(t?.common?.[lowerKey], language) || key;
                const valStr = toStr(value, language);
                
                return (
                  <div key={key} className="animate-in fade-in duration-300">
                    <p className="text-[10px] tracking-wider uppercase text-muted-foreground mb-1.5">{localizedLabel}</p>
                    <p className="text-base text-gray-900 font-normal">{valStr ? `${valStr} ${unit}` : '—'}</p>
                  </div>
                );
              })}
            </div>
          ) : isDimensionsString ? (
            <div className="max-w-2xl border-b border-gray-100 pb-10">
              <p className="text-[10px] tracking-wider uppercase text-muted-foreground mb-1.5">{tabDimLabel}</p>
              <p className="text-base text-gray-900 font-normal">{product.dimensions}</p>
            </div>
          ) : (
            <div className="border border-dashed border-gray-200 p-6 text-center text-sm text-muted-foreground bg-neutral-50/50 max-w-2xl">
              {dynamicCustomSizesTitle}
            </div>
          )}

          <div className="space-y-2 max-w-2xl pt-2 animate-in fade-in duration-700">
            <h4 className="text-xs uppercase font-medium tracking-widest text-black">
              {dynamicCustomSizesTitle}
            </h4>
            <p className="text-sm text-muted-foreground font-light leading-relaxed">
              {dynamicCustomSizesDesc}
            </p>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default ProductSpecs;
